<template>
  <div class="row">
    <div class="col-sm-12 col-md-6 p-2">
      <v-calendar
        id="compact-calendar" 
        class="compact-calendar"
        ref="events-calendar"
        color="red"
        :min-date="minimumDate"
        :attributes="calendarOptions"
        @did-move="calendarPageNavigated"
        @dayclick="calendarDateClicked"
        :locale="getNavigatorLanguage"
      >
        <template #header-prev-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-left"></i>
          </div>
        </template>
        <template #header-next-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-right"></i>
          </div>
        </template>
      </v-calendar>
    </div>
    <div class="col-sm-12 col-md-6 p-2">
      <div
        class="d-flex flex-column align-items-center justify-content-between h-100"
      >
        <div class="w-100">
          <div
            class="mb-2 d-flex flex-row justify-content-between align-items-center"
          >
            <span class="c-charcoal">{{
              $formatDate(
                selectedDate,
                'date-word-long',
                $t('PRODUCTS.LABELS.SELECT_A_DATE')
              )
            }}</span>
            <span>
              <i class="fa fa-clock-o me-1"></i>
              {{ meetingType.duration_minutes
              }}{{ $t('GENERAL.LABEL.MINUTES_ABBREVIATION') }}
            </span>
          </div>
          <div class="mb-3">
            <v-select
              :options="timezones"
              label="name"
              code="value"
              v-model="selectedTimezone"
              :reduce="r => r.value"
              @update:modelValue="loadData"
            >
            </v-select>
          </div>
          <div
            class="d-flex flex-row flex-wrap justify-content-center justify-content-md-between"
          >
            <div
              class="day-slot rounded bg-brand-color c-white text-center px-3 py-1 mb-2 mx-2 mx-md-0 pointer"
              :class="{ selected: selectedSlot === slot }"
              v-for="(slot, index) in selectedDailySlots"
              :key="index"
              @click="() => (selectedSlot = slot)"
            >
              {{ slot.format('LT') }}
            </div>
          </div>
        </div>
        <div class="w-100 mt-4">
          <HMButton
            v-if="selectedSlot"
            class="w-100 text-center"
            color="brand"
            size="md"
            @click="$emit('selectedSlot', { selectedSlot, selectedTimezone })"
            :disabled="isBooking"
            :loading="isBooking"
          >
            <span class="text-center w-100">
              {{
                $t('PRODUCTS.BUTTON.BOOK_SLOT', {
                  slot: $formatDate(selectedSlot, 'date-time'),
                })
              }}
            </span>
          </HMButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import colors from '@/styles/colors.scss?inline';
import { getNavigatorLanguage } from '@/utils/languages';
import timezones from '@/utils/timezones.ts';
import _ from 'lodash-es';
import { errorToMessage } from '@/utils/errors';
import { useStudioStore } from '@/stores';
import { mapState } from 'pinia';
import { useMarketingStore } from '@/stores';
import {toRaw} from "vue";

export default {
  name: 'PublicSchedulingCalendar',
  components: {},
  props: {
    studioURL: {
      type: String,
    },
    meetingType: {
      type: Object,
    },
    preselectedDate: {
      type: Date,
      default: () => new Date(),
    },
    isBooking: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
    }),
    timezones() {
      return timezones;
    },
    calendarOptions() {
      return [...this.calendarOptionsConfig, this.dottedDays];
    },
    selectedDailySlots() {
      if (this.isLoading) return [];
      const selectedDateSlots = this.timeSlots.filter(slot =>
        slot.isSame(this.selectedDate, 'day')
      );
      return selectedDateSlots;
    },
    getNavigatorLanguage,
    dottedDays() {
      return {
        dot: {
          style: {
            backgroundColor: this.studio?.primary_color || colors.coral,
          },
        },
        dates: this.dottedDates,
      };
    },
  },
  data() {
    return {
      selectedCalendarPage: {
        month: this.preselectedDate.getMonth(),
        year: this.preselectedDate.getFullYear(),
      },
      selectedDate: this.preselectedDate,
      minimumDate: new Date(),
      selectedSlot: null,
      selectedTimezone: null,
      dottedDates: [],
      calendarOptionsConfig: [
        {
          key: 'today',
          highlight: {
            class: 'todays-date-background',
            contentClass: 'todays-date-content',
          },
          dates: new Date(),
        },
      ],
      daySelected: false,
      isLoading: true,
      timeSlots: null,
    };
  },
  methods: {
    async calendarPageNavigated(page) {
      this.selectedCalendarPage = toRaw(page)[0];
      if (!this.selectedTimezone) return;
      this.selectedSlot = null;
      await this.loadData(false, false);
      this.daySelected = true;
    },
    calendarDateClicked(day) {
      this.selectedSlot = null;
      this.selectedDate = moment(day.date).toDate();
      this.daySelected = true;
    },
    async loadData(initial = false, shouldFocus = true) {
      this.isLoading = true;
      const y = this.selectedCalendarPage.year;
      const m = this.selectedCalendarPage.month - 1;
      const firstDay = moment(new Date(y, m, 1)).tz(this.selectedTimezone);
      const lastDay = moment(new Date(y, m + 1, 0))
        .add(1, 'days')
        .tz(this.selectedTimezone);
      try {
        this.dottedDates = [];
        
        let timeSlots = await useMarketingStore().getPublicMeetingTypeTimeSlots({
          studioURL: this.studioURL,
          slug: this.meetingType.slug,
          startDate: firstDay,
          endDate: lastDay,
          timezone: this.selectedTimezone,
        });
        if (initial && !timeSlots.length) {
          const extendedLastDay = moment(new Date(y, m + 3, 0))
            .add(1, 'days')
            .tz(this.selectedTimezone);

          timeSlots = await useMarketingStore().getPublicMeetingTypeTimeSlots({
              studioURL: this.studioURL,
              slug: this.meetingType.slug,
              startDate: lastDay,
              endDate: extendedLastDay,
              timezone: this.selectedTimezone,
          });
        }
        const now = moment().tz(this.selectedTimezone);
        this.timeSlots = timeSlots
          .map(slot => moment(slot).tz(this.selectedTimezone))
          .filter(slot => slot.isSameOrAfter(now));

        this.timeSlots.forEach(slot => {
          slot = slot.clone();
          if (slot.isSameOrAfter(now, 'day')) {
            if (!this.dottedDates.includes(slot.startOf('day').toString())) {
              this.dottedDates.push(slot.startOf('day').toString());
            }
          }
          if (!this.minimumDate || slot.isBefore(this.minimumDate)) {
            this.minimumDate = slot.toDate();
          }
        });
        this.dottedDates = this.dottedDates.map(day => new Date(day));
        
        if (this.dottedDates.length && (initial || !this.selectedDate)) {
          this.selectedDate = this.dottedDates[0];
          if (shouldFocus) {
            this.$refs['events-calendar'].focusDate(this.selectedDate);
          }
        } else if (!this.dottedDates.length && initial) {
          this.selectedDate = this.preselectedDate;
          if (shouldFocus) {
            this.$refs['events-calendar'].focusDate(this.selectedDate);
          }
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load available time slots',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.loadData(true);
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

:deep(#compact-calendar) { 
  --vc-border: transparent;
  // --vc-header-title-color: #{$charcoal};
  // --vc-weekday-color: #{$dark-gray};
  // --vc-day-content-disabled-color: #{$lightest-gray};
  // --vc-color: #{$medium-gray};
  // font-family: var(--brand-font, 'Poppins');
  // --vc-highlight-solid-bg: #{$charcoal} !important;
  width: 100%;
}

:deep(.vc-day-content)  {
  &:hover {
    background-color: $dark-gray !important;
    color: $white !important;
  }
  &:focus {
    background-color: var(--brand-color, $coral) !important;
    color: $white !important;
  }
}

:deep(.vc-day),
:deep(.vc-weekday)  {
  padding: 0.5rem;
  height: 47px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.vc-title)  {
  background: transparent;
  color: $dark-gray !important;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 5px 10px !important;
  font-family: var(--brand-font, 'Poppins');

  
  &:before {
    color: var(--brand-color, $coral);
    content: '';
    font-family: 'Font Awesome 5 Pro';
    font-weight: 400;
    display: inline-block;
    margin-right: 5px;
  }
}

/* move buttons away from borders */
:deep(.vc-header)  {
  padding-top: 12px;
  padding-left: 36px;
  padding-right: 36px;
  padding-bottom: 22px;
  margin: 0;
  height: auto;

  @media screen and (max-width: 992px) {
    padding-left: 20px;
    padding-right: 20px;
  }
}

:deep(.vc-highlight-bg-solid) {
  background: $charcoal;
}

:deep(.vc-arrow)  {
  background: transparent;
  width: 42px;
  height: 42px;
  border: 1px solid $medium-gray;
  &:hover {
    background-color: white;
  }
}

:deep(.vc-day) {
  // padding: 0.75rem 0;
}
</style>
